import { Component } from '@theme/component';

const recommendationMarkupCache = new Map();
const RECOMMENDATION_TIMEOUT_MS = 2200;
const SOURCE_SCORES = {
  complementary: 4000,
  related: 3000,
  curated: 2000,
  fallback: 1000,
};
const GENERIC_TAGS = new Set([
  'bundle size chart',
  'single',
  'multi pack',
  'updated_oct3',
  'updated oct3',
  'new arrival',
]);

class SmartCartUpsell extends Component {
  #userInteracted = false;
  #initializeVersion = 0;
  #refreshTimer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#markAsInteracted);
    this.addEventListener('keydown', this.#markAsInteracted);
    document.addEventListener('cart:update', this.#handleCartUpdate);
    this.#scheduleInitialize();
  }

  updatedCallback() {
    super.updatedCallback();
    // Horizon keeps this custom element and morphs its children in place.
    // Rebuild only after Horizon has finished applying the new cart section.
    this.#userInteracted = false;
    this.#scheduleInitialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#markAsInteracted);
    this.removeEventListener('keydown', this.#markAsInteracted);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    this.#initializeVersion += 1;
  }

  #markAsInteracted = () => {
    this.#userInteracted = true;
  };

  #handleCartUpdate = () => {
    this.#userInteracted = false;
    this.#scheduleInitialize();
  };

  #scheduleInitialize() {
    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    this.#refreshTimer = setTimeout(() => {
      this.#refreshTimer = undefined;
      this.#initialize();
    }, 0);
  }

  async #initialize() {
    const initializeVersion = ++this.#initializeVersion;
    const list = this.querySelector('[data-upsell-list]');
    if (!(list instanceof HTMLElement)) return;

    list.querySelectorAll('[data-upsell-source="complementary"], [data-upsell-source="related"]').forEach((card) => {
      card.remove();
    });

    const fallbackCards = Array.from(list.querySelectorAll('[data-smart-upsell-card]'));
    const context = this.#getCartContext();
    const fallbackResult = this.#prepareCards(fallbackCards, context);
    const mode = this.dataset.mode || 'smart';

    // The manually managed products are always the instant, no-network fallback.
    this.#showCards(list, fallbackResult.cards);

    if (mode !== 'smart' || !context.anchorProductIds.length) return;

    const smartCards = await this.#getSmartRecommendationCards(context.anchorProductIds);
    if (!this.isConnected || initializeVersion !== this.#initializeVersion || this.#userInteracted) return;

    const smartResult = this.#prepareCards([...smartCards, ...fallbackCards], context);
    if (smartResult.cards.length) this.#showCards(list, smartResult.cards);
  }

  #getCartContext() {
    const cartItems = this.closest('cart-items-component');
    const rows = Array.from(cartItems?.querySelectorAll('.cart-items__table-row[data-product-id]') || [])
      .filter((row) => row.dataset.isGift !== 'true');

    let storedVariantId = '';
    let storedProductId = '';
    try {
      storedVariantId = sessionStorage.getItem('bummer-cart-anchor-variant') || '';
      storedProductId = sessionStorage.getItem('bummer-cart-anchor-product') || '';
    } catch (_error) {
      // The first regular cart row is a safe fallback when storage is unavailable.
    }

    const anchorRow = rows.find((row) => row.dataset.variantId === storedVariantId)
      || rows.find((row) => row.dataset.productId === storedProductId)
      || rows[0];
    const maxAnchors = Math.max(1, Number(this.dataset.maxAnchors) || 3);
    const anchorProductIds = [anchorRow, ...rows]
      .map((row) => row?.dataset.productId || '')
      .filter((productId, index, productIds) => productId && productIds.indexOf(productId) === index)
      .slice(0, maxAnchors);

    return {
      anchorProductIds,
      cartProductIds: new Set(rows.map((row) => row.dataset.productId).filter(Boolean)),
      productTypes: new Set(rows.map((row) => this.#normalize(row.dataset.productType)).filter(Boolean)),
      tags: new Set(rows.flatMap((row) => this.#splitTags(row.dataset.productTags))),
    };
  }

  #prepareCards(cards, context) {
    const limit = Math.max(1, Number(this.dataset.limit) || 3);
    const cartTotal = Number(this.dataset.cartTotal) || 0;
    const maxPricePercent = Number(this.dataset.maxPricePercent) || 0;
    const excludedTags = new Set(this.#splitTags(this.dataset.excludedTags, ','));
    const acceptedCards = [];
    const seenProductIds = new Set();

    const rankedCards = cards
      .map((card, index) => ({ card, index, score: this.#scoreCard(card, context) }))
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .map(({ card }) => card);

    for (const card of rankedCards) {
      const productId = card.dataset.productId;
      if (!productId || seenProductIds.has(productId) || context.cartProductIds.has(productId)) continue;
      if (card.dataset.productTemplate === 'free-product') continue;

      const productTags = this.#splitTags(card.dataset.productTags);
      if (productTags.some((tag) => excludedTags.has(tag))) continue;

      const variants = Array.from(card.querySelectorAll('[data-upsell-variant]'));
      if (!variants.length) continue;

      const selectedVariant = variants[0];
      const selectedPrice = Number(selectedVariant.dataset.variantPriceCents) || 0;
      if (maxPricePercent > 0 && cartTotal > 0 && selectedPrice > (cartTotal * maxPricePercent) / 100) continue;

      if (card.dataset.hasSize === 'true') {
        this.#requireSizeSelection(card);
      } else {
        this.#selectVariant(card, selectedVariant);
      }

      seenProductIds.add(productId);
      acceptedCards.push(card);
      if (acceptedCards.length >= limit) break;
    }

    return { cards: acceptedCards };
  }

  #scoreCard(card, context) {
    const source = card.dataset.upsellSource || 'fallback';
    let score = SOURCE_SCORES[source] || SOURCE_SCORES.fallback;
    const productType = this.#normalize(card.dataset.productType);
    if (productType && context.productTypes.has(productType)) score += 250;

    for (const tag of this.#splitTags(card.dataset.productTags)) {
      if (!GENERIC_TAGS.has(tag) && context.tags.has(tag)) score += 35;
    }
    return score;
  }

  #splitTags(value, separator = '|') {
    return (value || '')
      .split(separator)
      .map((tag) => this.#normalize(tag))
      .filter(Boolean);
  }

  #normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  #selectVariant(card, variantOption) {
    const variantId = variantOption.dataset.variantId;
    const sizeLabel = variantOption.dataset.sizeLabel || '';
    const price = variantOption.dataset.variantPrice || '';
    const variantInput = card.querySelector('[js-card-product-variant-id]');
    const selectedLabels = card.querySelectorAll('[js-selected-variant-label], [js-card-product-variant-label]');
    const priceElement = card.querySelector('[js-card-product-price]');

    if (variantInput instanceof HTMLInputElement && variantId) variantInput.value = variantId;
    selectedLabels.forEach((label) => { label.textContent = sizeLabel; });
    if (priceElement && price) priceElement.textContent = price;

    card.querySelectorAll('[data-upsell-variant]').forEach((option) => {
      const selected = option === variantOption;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-selected', String(selected));
    });

    this.#setAddButtonState(card, false);
  }

  #requireSizeSelection(card) {
    const variantInput = card.querySelector('[js-card-product-variant-id]');
    const selectedLabels = card.querySelectorAll('[js-selected-variant-label], [js-card-product-variant-label]');

    if (variantInput instanceof HTMLInputElement) variantInput.value = '';
    selectedLabels.forEach((label) => { label.textContent = 'Choose size'; });
    card.querySelectorAll('[data-upsell-variant]').forEach((option) => {
      option.classList.remove('is-selected');
      option.setAttribute('aria-selected', 'false');
    });

    this.#setAddButtonState(card, true);
  }

  #setAddButtonState(card, disabled) {
    const addButton = card.querySelector('[js-upsell-atc-btn]');
    if (!(addButton instanceof HTMLButtonElement)) return;
    addButton.disabled = disabled;
    addButton.setAttribute('aria-disabled', String(disabled));
  }

  #showCards(list, cards) {
    const selectedCards = new Set(cards);
    list.querySelectorAll('[data-smart-upsell-card]').forEach((card) => {
      card.hidden = !selectedCards.has(card);
    });

    cards.forEach((card, index) => {
      list.append(card);
      card.hidden = false;
      card.setAttribute('aria-label', `${index + 1} / ${cards.length}`);
    });

    const status = this.querySelector('[data-upsell-status]');
    if (status) status.textContent = cards.length ? `${cards.length} recommendations available.` : '';
    this.hidden = cards.length === 0;
  }

  async #getSmartRecommendationCards(productIds) {
    const requests = ['complementary', 'related'].flatMap((intent) =>
      productIds.map((productId) => this.#getRecommendationCards(productId, intent))
    );
    const results = await Promise.allSettled(requests);
    return results.flatMap((result) => result.status === 'fulfilled' ? result.value : []);
  }

  async #getRecommendationCards(productId, intent) {
    const baseUrl = this.dataset.recommendationsUrl;
    if (!baseUrl) return [];

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('section_id', 'cart-upsell-recommendations');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);

    const cacheKey = url.toString();
    let markupPromise = recommendationMarkupCache.get(cacheKey);
    if (!markupPromise) {
      markupPromise = this.#fetchRecommendationMarkup(cacheKey);
      recommendationMarkupCache.set(cacheKey, markupPromise);
    }

    let markup;
    try {
      markup = await markupPromise;
    } catch (_error) {
      recommendationMarkupCache.delete(cacheKey);
      return [];
    }

    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
    const cards = Array.from(documentFragment.querySelectorAll('[data-smart-upsell-card]'));
    cards.forEach((card) => { card.dataset.upsellSource = intent; });
    return cards;
  }

  async #fetchRecommendationMarkup(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), RECOMMENDATION_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Recommendations failed with ${response.status}`);
      return await response.text();
    } finally {
      clearTimeout(timeout);
    }
  }
}

if (!customElements.get('smart-cart-upsell')) {
  customElements.define('smart-cart-upsell', SmartCartUpsell);
}
